import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { API_BASE_URL } from '../../../constants/api-urls';
import '../../../assets/styles/po_status_tracking.scss';

export default function POStatusTracking() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isContactVendorModalOpen, setIsContactVendorModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [poData, setPoData] = useState(null);
  const [quotationData, setQuotationData] = useState(null);
  const [rfqData, setRfqData] = useState(null);
  const [sellerDetails, setSellerDetails] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [timelineSteps, setTimelineSteps] = useState([]);
  const [attachments, setAttachments] = useState([]);

  // Master Data Hooks
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { currencyOptions } = useCurrencyMaster();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get poId from location.state
      const poIdFromState = location.state?.poId;
      if (!poIdFromState) {
        toast.error('Purchase Order ID not found');
        navigate('/purchase-order-list');
        return;
      }

      // Parse poId (handle both numeric and formatted strings like "PO-2024-123456")
      let poId = poIdFromState;
      if (typeof poIdFromState === 'string' && poIdFromState.startsWith('PO-')) {
        const match = poIdFromState.match(/(\d+)$/);
        if (match && match[1]) {
          poId = parseInt(match[1], 10);
        } else {
          toast.error('Invalid PO ID format');
          navigate('/purchase-order-list');
          return;
        }
      } else if (typeof poIdFromState === 'string') {
        poId = parseInt(poIdFromState, 10);
        if (isNaN(poId)) {
          toast.error('Invalid PO ID');
          navigate('/purchase-order-list');
          return;
        }
      }

      // Fetch PO data
      const poResponse = await CommonService.get(`/purchase-order/${poId}`);
      if (!poResponse || !poResponse.success || !poResponse.data) {
        toast.error('Failed to load purchase order data');
        navigate('/purchase-order-list');
        return;
      }

      const po = poResponse.data;
      setPoData(po);
      const items = po.line_items || po.po_data?.lineItems || [];
      setLineItems(items);
      
      // Extract attachments from PO data
      const poAttachments = po.attachments || po.po_data?.attachments || [];
      setAttachments(Array.isArray(poAttachments) ? poAttachments : []);

      // Fetch quotation data if quotation_id exists
      if (po.quotation_id) {
        try {
          const quotationResponse = await CommonService.get(`/quotation/${po.quotation_id}`);
          if (quotationResponse && quotationResponse.success && quotationResponse.data) {
            setQuotationData(quotationResponse.data);
            
            // Fetch RFQ data if rfq_id exists
            if (quotationResponse.data.rfq_id) {
              try {
                const rfqResponse = await CommonService.get(`/rfq/${quotationResponse.data.rfq_id}`);
                if (rfqResponse && rfqResponse.success && rfqResponse.data) {
                  setRfqData(rfqResponse.data);
                }
              } catch (rfqError) {
                console.error('Error fetching RFQ:', rfqError);
              }
            }

            // Fetch seller details if seller_company_id exists
            if (quotationResponse.data.seller_company_id) {
              try {
                const sellerResponse = await CommonService.get(`/company/profile/${quotationResponse.data.seller_company_id}`);
                if (sellerResponse && sellerResponse.success && sellerResponse.data) {
                  setSellerDetails(sellerResponse.data);
                }
              } catch (sellerError) {
                console.error('Error fetching seller details:', sellerError);
              }
            }
          }
        } catch (quotationError) {
          console.error('Error fetching quotation:', quotationError);
        }
      }

      // Calculate timeline steps based on PO status
      const timeline = calculateTimelineSteps(po);
      setTimelineSteps(timeline);

    } catch (error) {
      console.error('Error fetching PO data:', error);
      toast.error(error?.response?.data?.message || 'Failed to load purchase order data');
      navigate('/purchase-order-list');
    } finally {
      setLoading(false);
    }
  };

  const calculateTimelineSteps = (po) => {
    const steps = [
    {
      id: 1,
      title: 'PO Created',
      description: 'Purchase order created and submitted to seller',
        date: po.created_datetime ? formatDateTime(po.created_datetime) : (po.po_date ? formatDateTime(po.po_date) : null),
      status: 'completed',
      icon: 'document'
    },
    {
      id: 2,
      title: 'Pending Acceptance',
      description: 'Waiting for seller to accept the purchase order',
        expectedDate: po.expected_delivery_date ? `Expected by ${formatDate(po.expected_delivery_date)}` : null,
        status: po.po_status === 'Submitted' ? 'in_progress' : 'pending',
      icon: 'clock'
    },
    {
      id: 3,
      title: 'PO Accepted',
      description: 'Seller accepts the PO and confirms order',
      status: 'pending',
      icon: 'check'
    },
    {
      id: 4,
      title: 'Production',
      description: 'Items in production/preparation',
      status: 'pending',
        icon: 'gears'
    },
    {
      id: 5,
      title: 'Dispatched',
      description: 'Order dispatched for delivery',
      status: 'pending',
      icon: 'truck'
    },
    {
      id: 6,
      title: 'Delivered',
      description: 'Order delivered and received',
      status: 'pending',
      icon: 'package'
    }
  ];

    return steps;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle YYYY-MM-DD format to avoid timezone issues
      // If it's a date-only string (YYYY-MM-DD), parse it as local date
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
      }
      // For datetime strings, use standard parsing
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };

  const getUnitLabel = (uomId) => {
    if (!uomId || !attributeUnitOptions || attributeUnitOptions.length === 0) {
      return uomId || '';
    }
    const unitValueNum = typeof uomId === 'string' ? parseInt(uomId, 10) : uomId;
    const unitOption = attributeUnitOptions.find(opt => {
      const optValueNum = typeof opt.value === 'string' ? parseInt(opt.value, 10) : opt.value;
      return unitValueNum === optValueNum;
    });
    return unitOption ? unitOption.label : uomId || '';
  };

  // Helper function to get attachment file name
  const getAttachmentFileName = (attachment) => {
    if (typeof attachment === 'string') {
      return attachment;
    }
    if (attachment && attachment.name) {
      return attachment.name;
    }
    if (attachment && attachment.fileName) {
      return attachment.fileName;
    }
    if (attachment && attachment.file_path) {
      // Extract filename from path
      const pathParts = attachment.file_path.split(/[/\\]/);
      return pathParts[pathParts.length - 1];
    }
    if (attachment && attachment.accessPath) {
      // Extract filename from access path
      const pathParts = attachment.accessPath.split('/');
      return pathParts[pathParts.length - 1];
    }
    return 'Unknown file';
  };

  // Helper function to get attachment file path for download
  const getAttachmentFilePath = (attachment) => {
    if (typeof attachment === 'string') {
      return attachment;
    }
    if (attachment && attachment.file_path) {
      return attachment.file_path;
    }
    if (attachment && attachment.accessPath) {
      return attachment.accessPath;
    }
    if (attachment && attachment.fileName) {
      return attachment.fileName;
    }
    if (attachment && attachment.name) {
      return attachment.name;
    }
    return null;
  };

  // Handle attachment download
  const handleDownloadAttachment = async (attachment) => {
    try {
      const fileName = getAttachmentFileName(attachment);
      const filePath = getAttachmentFilePath(attachment);
      
      if (!poData || !poData.id) {
        toast.error('Purchase Order ID not found');
        return;
      }

      // Get API base URL from constants (already includes /api/v1)
      const apiBaseUrl = API_BASE_URL.replace('/api/v1', '') || 'http://localhost:3000';
      
      // Get auth token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Try to download via API endpoint
      const poId = poData.id || poData.purchase_order_id;
      const downloadUrl = `${apiBaseUrl}/api/v1/purchase-order/${poId}/attachment/${encodeURIComponent(fileName)}`;
      
      // Fetch file with authentication
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          toast.error('File not found. The file may not have been uploaded yet.');
        } else {
          toast.error('Failed to download file');
        }
        return;
      }

      // Get file blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download file');
    }
  };

  // Handle PO PDF download
  const handleDownloadPOPDF = async () => {
    try {
      if (!poData || !poData.id) {
        toast.error('Purchase Order ID not found');
        return;
      }

      const poId = poData.id || poData.purchase_order_id;
      const apiBaseUrl = API_BASE_URL.replace('/api/v1', '') || 'http://localhost:3000';
      const downloadUrl = `${apiBaseUrl}/api/v1/purchase-order/${poId}/download-pdf`;

      // Get auth token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Fetch PDF with authentication
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Get PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO-${poData.po_number || poId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PO PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PO PDF:', error);
      toast.error('Failed to download PO PDF');
    }
  };

  // Handle attachment view (open in new tab)
  const handleViewAttachment = async (attachment) => {
    try {
      const fileName = getAttachmentFileName(attachment);
      const filePath = getAttachmentFilePath(attachment);
      
      if (!poData || !poData.id) {
        toast.error('Purchase Order ID not found');
        return;
      }

      // Get API base URL from constants (already includes /api/v1)
      const apiBaseUrl = API_BASE_URL.replace('/api/v1', '') || 'http://localhost:3000';
      
      // Get auth token
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Try to view via API endpoint or static file serving
      const poId = poData.id || poData.purchase_order_id;
      
      // Try static file serving first (if file path is available)
      let viewUrl = null;
      if (filePath && (filePath.startsWith('/po-documents/') || filePath.startsWith('/media/'))) {
        // For static files, we can open directly (they may not require auth)
        viewUrl = `${apiBaseUrl}${filePath}`;
        window.open(viewUrl, '_blank');
      } else {
        // Use API endpoint with authentication
        const downloadUrl = `${apiBaseUrl}/api/v1/purchase-order/${poId}/attachment/${encodeURIComponent(fileName)}`;
        
        // Fetch file with authentication
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            toast.error('File not found. The file may not have been uploaded yet.');
          } else {
            toast.error('Failed to open file');
          }
          return;
        }

        // Get file blob and create object URL for viewing
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Clean up the URL after a delay (browser will handle it when tab closes)
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      }
    } catch (error) {
      console.error('Error viewing attachment:', error);
      toast.error('Failed to open file');
    }
  };

  // Helper function to check if trade is international
  const isInternationalTrade = (tradeScenario) => {
    if (!tradeScenario) return false;
    return tradeScenario.includes('Abroad') || 
           tradeScenario.includes('Import') || 
           tradeScenario.includes('Export') ||
           tradeScenario === 'Third Country (Abroad → Abroad)';
  };

  const calculateTotals = () => {
    if (!lineItems || lineItems.length === 0 || !poData) {
      return {
        subtotal: 0,
        totalDiscount: 0,
        taxableAmount: 0,
        igst: 0,
        cgst: 0,
        sgst: 0,
        cess: 0,
        basicCustomsDuty: 0,
        socialWelfareSurcharge: 0,
        assessableValue: 0,
        igstOnAssessable: 0,
        tax: 0,
        grandTotal: 0
      };
    }

    const tradeScenario = poData.trade_scenario || poData.tradeScenario || '';
    const exportType = poData.export_type || poData.exportType || '';
    const cifConfig = poData.cif_config || poData.cifConfig || {};

    // Calculate subtotal from line items
    const subtotal = lineItems.reduce((sum, item) => {
      const itemSubtotal = parseFloat(item.subtotal || item.quantity * (item.unit_price || item.unitPrice) || 0);
      return sum + itemSubtotal;
    }, 0);

    // Calculate total discount amount
    const totalDiscount = lineItems.reduce((sum, item) => {
      return sum + parseFloat(item.discount_amount || item.discountAmount || 0);
    }, 0);

    // Taxable amount (FOB value) is subtotal minus discount
    const taxableAmount = subtotal - totalDiscount;

    // Helper function to get tax amount for an item
    const getItemTaxable = (item) => {
      const itemSubtotal = parseFloat(item.subtotal || item.quantity * (item.unit_price || item.unitPrice) || 0);
      const itemDiscount = parseFloat(item.discount_amount || item.discountAmount || 0);
      return parseFloat(item.fob_value || item.fobValue || (itemSubtotal - itemDiscount));
    };

    // Calculate CGST (only for intra-state)
    const cgst = lineItems.reduce((sum, item) => {
      let cgstAmount = parseFloat(item.cgst_amount || item.cgstAmount || 0);
      if ((!cgstAmount || cgstAmount === 0) && (item.cgst || item.cgstPercent)) {
        const itemTaxable = getItemTaxable(item);
        const cgstPercent = parseFloat(item.cgst || item.cgstPercent || 0);
        if (cgstPercent > 0 && itemTaxable > 0) {
          cgstAmount = itemTaxable * (cgstPercent / 100);
        }
      }
      return sum + cgstAmount;
    }, 0);

    // Calculate SGST (only for intra-state)
    const sgst = lineItems.reduce((sum, item) => {
      let sgstAmount = parseFloat(item.sgst_amount || item.sgstAmount || 0);
      if ((!sgstAmount || sgstAmount === 0) && (item.sgst || item.sgstPercent)) {
        const itemTaxable = getItemTaxable(item);
        const sgstPercent = parseFloat(item.sgst || item.sgstPercent || 0);
        if (sgstPercent > 0 && itemTaxable > 0) {
          sgstAmount = itemTaxable * (sgstPercent / 100);
        }
      }
      return sum + sgstAmount;
    }, 0);

    // Calculate IGST (for inter-state, export, and import)
    const igst = lineItems.reduce((sum, item) => {
      let igstAmount = parseFloat(item.igst_amount || item.igstAmount || 0);
      if ((!igstAmount || igstAmount === 0) && (item.igst || item.igstPercent)) {
        const itemTaxable = getItemTaxable(item);
        const igstPercent = parseFloat(item.igst || item.igstPercent || 0);
        if (igstPercent > 0 && itemTaxable > 0) {
          igstAmount = itemTaxable * (igstPercent / 100);
        }
      }
      return sum + igstAmount;
    }, 0);

    // Calculate CESS
    const cess = lineItems.reduce((sum, item) => {
      let cessAmount = parseFloat(item.cess_amount || item.cessAmount || 0);
      if ((!cessAmount || cessAmount === 0) && (item.cess || item.cessPercent)) {
        const itemTaxable = getItemTaxable(item);
        const cessPercent = parseFloat(item.cess || item.cessPercent || 0);
        if (cessPercent > 0 && itemTaxable > 0) {
          cessAmount = itemTaxable * (cessPercent / 100);
        }
      }
      return sum + cessAmount;
    }, 0);

    // Calculate FOB and CIF for import scenarios
    const fobValue = taxableAmount;
    const shippingCharges = parseFloat(cifConfig.freightPercent || 0) > 0 
      ? fobValue * (parseFloat(cifConfig.freightPercent) / 100) 
      : 0;
    const insuranceCharges = parseFloat(cifConfig.insurancePercent || 0) > 0 
      ? fobValue * (parseFloat(cifConfig.insurancePercent) / 100) 
      : 0;
    const handlingCharges = parseFloat(cifConfig.handlingFixed || 0) || 0;
    const cifValue = isInternationalTrade(tradeScenario) && tradeScenario.includes('Import')
      ? fobValue + shippingCharges + insuranceCharges + handlingCharges
      : 0;

    // Calculate Basic Customs Duty (BCD) for import
    const basicCustomsDuty = (isInternationalTrade(tradeScenario) && tradeScenario.includes('Import'))
      ? (() => {
          // Calculate weighted average customs duty rate
          const totalFOB = lineItems.reduce((sum, item) => getItemTaxable(item), 0);
          if (totalFOB === 0) return 0;
          const weightedCustomsRate = lineItems.reduce((sum, item) => {
            const itemTaxable = getItemTaxable(item);
            const customsPercent = parseFloat(item.customs || item.customsDuty || 0);
            return sum + ((itemTaxable / totalFOB) * (customsPercent / 100));
          }, 0);
          return cifValue * weightedCustomsRate;
        })()
      : 0;

    // Social Welfare Surcharge (10% of BCD)
    const socialWelfareSurcharge = basicCustomsDuty * 0.10;

    // Assessable Value (for import: CIF + BCD + SWS)
    const assessableValue = (isInternationalTrade(tradeScenario) && tradeScenario.includes('Import'))
      ? cifValue + basicCustomsDuty + socialWelfareSurcharge
      : taxableAmount;

    // IGST on Assessable Value (for import: 18% on assessable value)
    const igstOnAssessable = (isInternationalTrade(tradeScenario) && tradeScenario.includes('Import'))
      ? assessableValue * 0.18
      : 0;

    // Calculate tax for Third Country scenario
    const tax = (tradeScenario === 'Third Country (Abroad → Abroad)')
      ? lineItems.reduce((sum, item) => {
          const itemTaxable = getItemTaxable(item);
          const taxPercent = parseFloat(item.tax || 0);
          return sum + (itemTaxable * (taxPercent / 100));
        }, 0)
      : 0;

    // Calculate grand total based on trade scenario
    let grandTotal = 0;
    if (tradeScenario === 'Third Country (Abroad → Abroad)') {
      grandTotal = taxableAmount + tax;
    } else if (tradeScenario === 'IN→ Export from India (India → Abroad)') {
      // For export: IGST and CESS are calculated on FOB Value
      if (exportType === 'Non-LUT - IGST Applicable') {
        grandTotal = taxableAmount + igst + cess;
      } else {
        // LUT: No tax
        grandTotal = taxableAmount;
      }
    } else if (isInternationalTrade(tradeScenario) && tradeScenario.includes('Import')) {
      // For import: Assessable Value + IGST on Assessable Value + CESS on Assessable Value
      // Calculate weighted average CESS rate
      const totalFOB = lineItems.reduce((sum, item) => getItemTaxable(item), 0);
      const weightedCessRate = totalFOB > 0 
        ? lineItems.reduce((sum, item) => {
            const itemTaxable = getItemTaxable(item);
            const cessPercent = parseFloat(item.cess || 0);
            return sum + ((itemTaxable / totalFOB) * (cessPercent / 100));
          }, 0)
        : 0;
      const cessOnAssessable = assessableValue * weightedCessRate;
      grandTotal = assessableValue + igstOnAssessable + cessOnAssessable;
    } else if (tradeScenario === 'IN Inter-State (Between Indian States)') {
      grandTotal = taxableAmount + igst + cess;
    } else {
      // Intra-state: CGST + SGST + CESS
      grandTotal = taxableAmount + cgst + sgst + cess;
    }

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      igst,
      cgst,
      sgst,
      cess,
      basicCustomsDuty,
      socialWelfareSurcharge,
      assessableValue,
      igstOnAssessable,
      tax,
      grandTotal
    };
  };

  // Helper function to get currency symbol from currency code
  const getCurrencySymbol = (currencyCode) => {
    if (!currencyCode) return '₹'; // Default to INR symbol
    if (currencyOptions && currencyOptions.length > 0) {
      const foundByCode = currencyOptions.find(opt => 
        opt.currencyCode === String(currencyCode).toUpperCase() ||
        String(opt.value) === String(currencyCode) ||
        opt.currencyCode === String(currencyCode)
      );
      if (foundByCode && foundByCode.currencySymbol) {
        return foundByCode.currencySymbol;
      }
    }
    // Fallback: Try to get symbol from Intl.NumberFormat
    try {
      const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
        currency: String(currencyCode).toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      const parts = formatter.formatToParts(0);
      const symbolPart = parts.find(part => part.type === 'currency');
      return symbolPart ? symbolPart.value : '₹';
    } catch (e) {
      return '₹'; // Default to INR symbol
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    if (!amount && amount !== 0) {
      const symbol = getCurrencySymbol(currency);
      return `${symbol}0.00`;
    }
    
    // Get currency symbol
    const symbol = getCurrencySymbol(currency);
    
    // Format number with 2 decimal places
    const formattedNumber = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    
    // Return formatted amount with currency symbol
    return `${symbol}${formattedNumber}`;
  };

  // Early return if loading
  if (loading) {
    return (
      <div className="po-status-tracking">
        <div className="po-status-main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <p>Loading purchase order data...</p>
        </div>
      </div>
    );
  }

  // Early return if no PO data
  if (!poData) {
    return (
      <div className="po-status-tracking">
        <div className="po-status-main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <p>Purchase order not found.</p>
        </div>
      </div>
    );
  }

  const getTimelineIcon = (iconType) => {
    switch (iconType) {
      case 'document':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
            <path d="M10 9H8"></path>
            <path d="M16 13H8"></path>
            <path d="M16 17H8"></path>
          </svg>
        );
      case 'clock':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 6v6l4 2"></path>
            <circle cx="12" cy="12" r="10"></circle>
          </svg>
        );
      case 'check':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
        );
      case 'gears':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
            <path d="M12 22V12"></path>
            <polyline points="3.29 7 12 12 20.71 7"></polyline>
            <path d="m7.5 4.27 9 5.15"></path>
          </svg>
        );
      case 'truck':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
            <path d="M15 18H9"></path>
            <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
            <circle cx="17" cy="18" r="2"></circle>
            <circle cx="7" cy="18" r="2"></circle>
          </svg>
        );
      case 'package':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="po-status-tracking">
        <div className="po-status-main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <p>Loading purchase order data...</p>
        </div>
      </div>
    );
  }

  if (!poData) {
    return (
      <div className="po-status-tracking">
        <div className="po-status-main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <p>Purchase order not found.</p>
        </div>
      </div>
    );
  }

  // Safe access to poData properties
  // At this point, poData is guaranteed to be non-null (we've checked above)
  const displayPoNumber = poData.po_number || 'N/A';
  const displayPoDate = poData.po_date ? formatDate(poData.po_date) : 'N/A';
  const displayExpectedDelivery = poData.expected_delivery_date ? formatDate(poData.expected_delivery_date) : 'N/A';
  const displayStatus = poData.po_status || 'Draft';
  const displayTradeType = poData.trade_type || 'N/A';
  const displayTradeLocation = poData.trade_location || 'N/A';
  const displayPaymentTerms = poData.payment_terms || 'N/A';
  const displayCurrency = poData.currency || 'INR';
  const vendorCompany = sellerDetails?.company_name || quotationData?.seller_company_name || 'N/A';

  // Extract seller contact details
  const getSellerContactDetails = () => {
    if (!sellerDetails) {
      return {
        email: quotationData?.seller_email || 'N/A',
        phone: quotationData?.seller_phone || 'N/A',
        address: quotationData?.seller_address || poData?.seller_address || 'N/A'
      };
    }

    // Parse contact_details JSON
    let contactDetails = {};
    if (sellerDetails.contact_details) {
      try {
        contactDetails = typeof sellerDetails.contact_details === 'string' 
          ? JSON.parse(sellerDetails.contact_details) 
          : sellerDetails.contact_details;
      } catch (e) {
        console.error('Error parsing contact_details:', e);
      }
    }

    // Parse address JSON
    let address = 'N/A';
    if (sellerDetails.address) {
      try {
        const addr = typeof sellerDetails.address === 'string' 
          ? JSON.parse(sellerDetails.address) 
          : sellerDetails.address;
        if (typeof addr === 'object' && addr.address) {
          // Format address nicely
          const parts = [
            addr.address,
            addr.city,
            addr.state,
            addr.country,
            addr.pincode
          ].filter(Boolean);
          address = parts.join(', ') || 'N/A';
        } else if (typeof addr === 'string') {
          address = addr;
        }
      } catch (e) {
        address = typeof sellerDetails.address === 'string' ? sellerDetails.address : 'N/A';
      }
    } else if (quotationData?.seller_address) {
      address = quotationData.seller_address;
    } else if (poData?.seller_address) {
      address = poData.seller_address;
    }

    return {
      email: contactDetails.contact_email || quotationData?.seller_email || 'N/A',
      phone: contactDetails.contact_number || quotationData?.seller_phone || 'N/A',
      address: address
    };
  };

  const sellerContact = getSellerContactDetails();

  // Determine if trade type is IGST (inter-state) or CGST+SGST (intra-state)
  const isIGSTTrade = displayTradeType && (displayTradeType.includes('IGST') || displayTradeType.includes('Inter-State'));
  const taxColumnsCount = isIGSTTrade ? 1 : 2; // IGST = 1 column, CGST+SGST = 2 columns
  // Total columns: #, Description, HSN, Qty, UOM, Unit Price, Disc %, Tax columns, CESS %, Total
  // = 1 + 1 + 1 + 1 + 1 + 1 + 1 + taxColumnsCount + 1 + 1 = 8 + taxColumnsCount
  const totalColumnsCount = 8 + taxColumnsCount;

  // Calculate totals and progress after we know poData exists
  const totals = calculateTotals();
  const progressPercentage = timelineSteps && timelineSteps.length > 0 
    ? Math.round((timelineSteps.filter(step => step.status === 'completed').length / timelineSteps.length) * 100)
    : 0;

  return (
    <div className="po-status-tracking">
      <div className="po-status-main-content">
        {/* Header Section */}
        <div className="po-status-header">
          <div className="po-status-header-left">
            <nav aria-label="breadcrumb" className="po-status-breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a 
                    href="#" 
                    className="breadcrumb-link" 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/purchase-order-list');
                    }}
                  >
                    Order Details
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  PO Status Tracking
                </li>
              </ol>
            </nav>
            <h1 className="po-status-main-title">Purchase Order: {displayPoNumber}</h1>
            <p className="po-status-subtitle">Track your purchase order status and updates</p>
          </div>
          <div className="po-status-header-right">
            <div className="po-status-header-buttons">
              <button className="po-status-back-btn" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                Back
              </button>
              <button 
                className="po-status-btn po-status-btn-secondary"
                onClick={() => setIsMessageModalOpen(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                Send Message
              </button>
              <button 
                className="po-status-btn po-status-btn-secondary"
                onClick={handleDownloadPOPDF}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Download PO
              </button>
            </div>
            <div className="po-status-badge-wrapper">
              <span className={`po-status-badge ${displayStatus === 'Submitted' ? 'po-status-badge-pending' : 'po-status-badge-pending'}`}>
                {displayStatus.toUpperCase()}
              </span>
              <div className="po-status-trade-info">
                <div className="po-status-trade-type-badge">
                  <span className="po-badge">{displayTradeType}</span>
                </div>
                <div className="po-status-trade-location">
                  {displayTradeLocation}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message Alert */}
        {displayStatus === 'Submitted' && (
        <div className="po-status-alert po-status-alert-success">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-status-alert-icon" aria-hidden="true">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
          <div className="po-status-alert-content">
            <h3 className="po-status-alert-title">Purchase Order Submitted Successfully!</h3>
            <p className="po-status-alert-message">
                Your PO has been sent to {vendorCompany}. You will be notified once the seller accepts the order.
            </p>
          </div>
        </div>
        )}

        {/* Order Progress Section - Full Width */}
        <div className="po-status-card po-status-card-full-width">
          <h3 className="po-status-card-title">Order Progress</h3>
          <div className="po-status-progress-wrapper">
            <div className="po-status-progress-bar">
              <div 
                className="po-status-progress-fill" 
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <span className="po-status-progress-text">{progressPercentage}% Complete</span>
          </div>
        </div>

        <div className="po-status-content-grid">
          {/* Left Column - Main Content */}
          <div className="po-status-content-left">
            {/* Order Timeline Section */}
            <div className="po-status-card po-status-timeline-card">
              <div className="po-status-card-header">
                <h4 className="po-status-card-title">Order Timeline</h4>
                <p className="po-status-card-description">Track the progress of your purchase order</p>
              </div>
              <div className="po-status-timeline-content">
                <div className="po-status-timeline">
                  {timelineSteps.map((step, index) => (
                    <div 
                      key={step.id} 
                      className="po-status-timeline-item"
                    >
                      <div className="po-status-timeline-icon-wrapper">
                        <div className={`po-status-timeline-icon po-status-timeline-icon-${step.status}`}>
                          {getTimelineIcon(step.icon)}
                        </div>
                        {index < timelineSteps.length - 1 && (
                          <div className={`po-status-timeline-line po-status-timeline-line-${step.status}`}></div>
                        )}
                      </div>
                      <div className="po-status-timeline-item-content">
                        <div className="po-status-timeline-header">
                          <h4 className={`po-status-timeline-title po-status-timeline-title-${step.status}`}>
                            {step.title}
                          </h4>
                          {step.status !== 'pending' && (
                            <span className={`po-status-timeline-badge po-status-timeline-badge-${step.status}`}>
                              {step.status === 'completed' ? 'Completed' : 'In Progress'}
                            </span>
                          )}
                        </div>
                        <p className="po-status-timeline-description">{step.description}</p>
                        {step.date && (
                          <p className="po-status-timeline-date">{step.date}</p>
                        )}
                        {step.expectedDate && (
                          <p className="po-status-timeline-expected">{step.expectedDate}</p>
                        )}
                        {step.status === 'pending' && !step.date && !step.expectedDate && (
                          <p className="po-status-timeline-pending">Pending</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="po-status-content-right">
            {/* Order Summary Card */}
            <div className="po-status-card po-status-summary-card">
              <div className="po-status-card-header">
                <h4 className="po-status-card-title">Order Summary</h4>
              </div>
              <div className="po-status-summary-list">
                <div className="po-status-summary-item">
                  <p className="po-status-summary-label">PO Number</p>
                  <p className="po-status-summary-value">{displayPoNumber}</p>
                </div>
                <div className="po-status-summary-divider"></div>
                <div className="po-status-summary-item">
                  <p className="po-status-summary-label">PO Date</p>
                  <p className="po-status-summary-value">{displayPoDate}</p>
                </div>
                <div className="po-status-summary-divider"></div>
                <div className="po-status-summary-item">
                  <p className="po-status-summary-label">Expected Delivery</p>
                  <p className="po-status-summary-value">{displayExpectedDelivery}</p>
                </div>
                <div className="po-status-summary-divider"></div>
                <div className="po-status-summary-item">
                  <p className="po-status-summary-label">Total Amount</p>
                  <p className="po-status-summary-amount">{formatCurrency(totals.grandTotal, displayCurrency)}</p>
                </div>
                <div className="po-status-summary-divider"></div>
                <div className="po-status-summary-item">
                  <p className="po-status-summary-label">Payment Terms</p>
                  <p className="po-status-summary-value po-status-summary-value-sm">{displayPaymentTerms}</p>
                </div>
              </div>
            </div>

            {/* Vendor Details Card */}
            <div className="po-status-card po-status-vendor-card">
              <div className="po-status-card-header">
                <h4 className="po-status-card-title po-status-card-title-with-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                    <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                    <path d="M10 6h4"></path>
                    <path d="M10 10h4"></path>
                    <path d="M10 14h4"></path>
                    <path d="M10 18h4"></path>
                  </svg>
                  Vendor Details
                </h4>
              </div>
              <div className="po-status-vendor-content">
                <div className="po-status-vendor-item">
                  <p className="po-status-vendor-label">Company</p>
                  <p className="po-status-vendor-value">{vendorCompany}</p>
                </div>
                <button 
                  className="po-status-contact-btn"
                  onClick={() => setIsContactVendorModalOpen(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"></path>
                  </svg>
                  Contact Vendor
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Ordered Items Table - Full Width (col-12) - Below Order Timeline */}
        <div className="po-status-card po-status-items-card po-status-card-full-width">
          <div className="po-status-card-header">
            <h4 className="po-status-card-title">Ordered Items</h4>
          </div>
          <div className="po-status-items-content">
            <div className="po-status-table-overflow">
              <div className="po-status-table-container">
                <table className="po-status-table">
                  <thead>
                    <tr>
                      <th className="po-status-table-head po-status-table-head-number">#</th>
                      <th className="po-status-table-head po-status-table-head-description">Description</th>
                      <th className="po-status-table-head po-status-table-head-hsn">HSN</th>
                      <th className="po-status-table-head po-status-table-head-qty">Qty</th>
                      <th className="po-status-table-head po-status-table-head-uom">UOM</th>
                      <th className="po-status-table-head po-status-table-head-price">Unit Price</th>
                      <th className="po-status-table-head po-status-table-head-disc">Disc %</th>
                      {isIGSTTrade ? (
                      <th className="po-status-table-head po-status-table-head-tax">IGST %</th>
                      ) : (
                        <>
                          <th className="po-status-table-head po-status-table-head-tax">CGST %</th>
                          <th className="po-status-table-head po-status-table-head-tax">SGST %</th>
                        </>
                      )}
                      <th className="po-status-table-head po-status-table-head-tax">CESS %</th>
                      <th className="po-status-table-head po-status-table-head-total">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems && lineItems.length > 0 ? (
                      lineItems.map((item, index) => {
                        const unitPrice = parseFloat(item.unit_price || 0);
                        const quantity = parseFloat(item.quantity || 0);
                        const discount = parseFloat(item.discount || 0);
                        const igst = parseFloat(item.igst || 0);
                        const cgst = parseFloat(item.cgst || 0);
                        const sgst = parseFloat(item.sgst || 0);
                        const cess = parseFloat(item.cess || 0);
                        const total = parseFloat(item.total || 0);
                        const hsnCode = item.hsn_code || item.hsn || 'N/A';
                        const uom = getUnitLabel(item.uom);
                        
                        return (
                          <tr key={item.line_item_id || item.id || index} className="po-status-table-row">
                        <td className="po-status-table-cell">{index + 1}</td>
                            <td className="po-status-table-cell">{item.description || 'N/A'}</td>
                            <td className="po-status-table-cell">{hsnCode}</td>
                            <td className="po-status-table-cell">{quantity}</td>
                            <td className="po-status-table-cell">{uom}</td>
                            <td className="po-status-table-cell">{formatCurrency(unitPrice, displayCurrency)}</td>
                            <td className="po-status-table-cell">{discount}%</td>
                            {isIGSTTrade ? (
                              <td className="po-status-table-cell">{igst}%</td>
                            ) : (
                              <>
                                <td className="po-status-table-cell">{cgst}%</td>
                                <td className="po-status-table-cell">{sgst}%</td>
                              </>
                            )}
                            <td className="po-status-table-cell">{cess}%</td>
                            <td className="po-status-table-cell po-status-table-cell-total">{formatCurrency(total, displayCurrency)}</td>
                      </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={totalColumnsCount} className="po-status-table-cell" style={{ textAlign: 'center', padding: '20px' }}>
                          No line items found
                        </td>
                      </tr>
                    )}
                    {/* Summary Rows */}
                    {lineItems && lineItems.length > 0 && (
                      <>
                    <tr className="po-status-table-summary-row">
                      <td colSpan="1"></td>
                      <td className="po-status-table-summary-label">Subtotal:</td>
                          <td colSpan={5 + taxColumnsCount + 1}></td>
                          <td className="po-status-table-summary-value">{formatCurrency(totals.subtotal, displayCurrency)}</td>
                    </tr>
                    <tr className="po-status-table-summary-row">
                      <td colSpan="1"></td>
                      <td className="po-status-table-summary-label">Total Discount:</td>
                          <td colSpan={5 + taxColumnsCount + 1}></td>
                          <td className="po-status-table-summary-value">- {formatCurrency(totals.totalDiscount, displayCurrency)}</td>
                    </tr>
                    <tr className="po-status-table-summary-row">
                      <td colSpan="1"></td>
                      <td className="po-status-table-summary-label">Taxable Amount:</td>
                          <td colSpan={5 + taxColumnsCount + 1}></td>
                          <td className="po-status-table-summary-value">{formatCurrency(totals.taxableAmount, displayCurrency)}</td>
                    </tr>
                        {isIGSTTrade ? (
                    <tr className="po-status-table-summary-row">
                      <td colSpan="1"></td>
                      <td className="po-status-table-summary-label">IGST:</td>
                            <td colSpan={5 + taxColumnsCount + 1}></td>
                            <td className="po-status-table-summary-value">{formatCurrency(totals.igst, displayCurrency)}</td>
                    </tr>
                        ) : (
                          <>
                            <tr className="po-status-table-summary-row">
                              <td colSpan="1"></td>
                              <td className="po-status-table-summary-label">CGST:</td>
                              <td colSpan={5 + taxColumnsCount + 1}></td>
                              <td className="po-status-table-summary-value">{formatCurrency(totals.cgst, displayCurrency)}</td>
                            </tr>
                            <tr className="po-status-table-summary-row">
                              <td colSpan="1"></td>
                              <td className="po-status-table-summary-label">SGST:</td>
                              <td colSpan={5 + taxColumnsCount + 1}></td>
                              <td className="po-status-table-summary-value">{formatCurrency(totals.sgst, displayCurrency)}</td>
                            </tr>
                          </>
                        )}
                    <tr className="po-status-table-summary-row">
                      <td colSpan="1"></td>
                      <td className="po-status-table-summary-label">CESS:</td>
                          <td colSpan={5 + taxColumnsCount + 1}></td>
                          <td className="po-status-table-summary-value">{formatCurrency(totals.cess, displayCurrency)}</td>
                    </tr>
                    <tr className="po-status-table-summary-row po-status-table-grand-total">
                      <td colSpan="1"></td>
                      <td className="po-status-table-summary-label">
                        <span className="font-semibold">Grand Total:</span>
                      </td>
                          <td colSpan={5 + taxColumnsCount + 1}></td>
                      <td className="po-status-table-summary-value">
                            <span className="font-semibold">{formatCurrency(totals.grandTotal, displayCurrency)}</span>
                      </td>
                    </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Related Documents Section - Below Ordered Items */}
        {attachments && attachments.length > 0 && (
        <div className="po-status-card po-status-card-full-width">
          <div className="po-status-card-header">
            <h4 className="po-status-card-title po-status-card-title-with-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                <path d="M10 9H8"></path>
                <path d="M16 13H8"></path>
                <path d="M16 17H8"></path>
              </svg>
              Related Documents
            </h4>
          </div>
          <div className="po-status-documents-content">
              <div className="po-status-attachments-list">
                {attachments.map((attachment, index) => {
                  const fileName = getAttachmentFileName(attachment);
                  const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
                  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
                  const isPdf = fileExtension === 'pdf';
                  
                  return (
                    <div key={index} className="po-status-attachment-item">
                      <div className="po-status-attachment-info">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-status-attachment-icon">
                          {isPdf ? (
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          ) : isImage ? (
                            <>
                              <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                              <circle cx="9" cy="9" r="2"></circle>
                              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                            </>
                          ) : (
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          )}
                        </svg>
                        <span className="po-status-attachment-name" title={fileName}>
                          {fileName}
                        </span>
                      </div>
                      <div className="po-status-attachment-actions">
                        <button
                          className="po-status-attachment-btn po-status-attachment-btn-view"
                          onClick={() => handleViewAttachment(attachment)}
                          title="View file"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                          View
                        </button>
                        <button
                          className="po-status-attachment-btn po-status-attachment-btn-download"
                          onClick={() => handleDownloadAttachment(attachment)}
                          title="Download file"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 15V3"></path>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <path d="m7 10 5 5 5-5"></path>
                </svg>
                          Download
              </button>
            </div>
          </div>
                  );
                })}
        </div>
            </div>
          </div>
        )}

        {/* Warning Alert - Below Related Documents */}
        <div className="po-status-alert po-status-alert-warning-card">
          <div className="po-status-alert-warning-content">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-status-alert-warning-icon" aria-hidden="true">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" x2="12" y1="8" y2="12"></line>
              <line x1="12" x2="12.01" y1="16" y2="16"></line>
            </svg>
            <div className="po-status-alert-warning-text">
              <h3 className="po-status-alert-warning-title">Waiting for Vendor Acceptance</h3>
              <p className="po-status-alert-warning-message">
                The vendor has been notified about this purchase order. You will receive a notification once they accept or if they have any questions.
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* Send Message Modal */}
      {isMessageModalOpen && (
        <div className="po-status-modal-overlay" onClick={() => setIsMessageModalOpen(false)}>
          <div className="po-status-modal" onClick={(e) => e.stopPropagation()}>
            <div className="po-status-modal-header">
              <h3 className="po-status-modal-title">Send Message</h3>
              <button 
                className="po-status-modal-close"
                onClick={() => setIsMessageModalOpen(false)}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="po-status-modal-content">
              <div className="po-status-modal-recipient">
                <p className="po-status-modal-recipient-label">To:</p>
                <p className="po-status-modal-recipient-name">{vendorCompany}</p>
              </div>
              <div className="po-status-modal-po-info">
                <div className="po-status-modal-po-info-item">
                  <label className="po-status-modal-po-info-label">PO Number:</label>
                  <p className="po-status-modal-po-info-value">{displayPoNumber}</p>
                </div>
                <div className="po-status-modal-po-info-item">
                  <label className="po-status-modal-po-info-label">PO Date:</label>
                  <p className="po-status-modal-po-info-value">{displayPoDate}</p>
                </div>
                <div className="po-status-modal-po-info-item">
                  <label className="po-status-modal-po-info-label">Expected Delivery:</label>
                  <p className="po-status-modal-po-info-value">{displayExpectedDelivery}</p>
                </div>
                <div className="po-status-modal-po-info-item">
                  <label className="po-status-modal-po-info-label">Total Amount:</label>
                  <p className="po-status-modal-po-info-value">{formatCurrency(totals.grandTotal, displayCurrency)}</p>
                </div>
              </div>
              <div className="po-status-modal-message-field">
                <label htmlFor="message-text" className="po-status-modal-label">Message</label>
                <textarea
                  id="message-text"
                  className="po-status-modal-textarea"
                  rows="3"
                  placeholder="Type your message here..."
                  value={messageText}
                  onChange={(e) => {
                    if (e.target.value.length <= 1000) {
                      setMessageText(e.target.value);
                    }
                  }}
                  maxLength={1000}
                />
                <p className="po-status-modal-char-count">
                  {messageText.length} / 1000 characters
                </p>
              </div>
            </div>
            <div className="po-status-modal-footer">
              <button 
                className="po-status-btn po-status-btn-secondary"
                onClick={() => {
                  setIsMessageModalOpen(false);
                  setMessageText('');
                }}
              >
                Cancel
              </button>
              <button 
                className="po-status-btn po-status-btn-primary"
                onClick={async () => {
                  if (messageText.trim() && poData && poData.id) {
                    try {
                      setSendingMessage(true);
                      
                      // Get PO ID (handle both numeric and formatted strings)
                      let poId = poData.id;
                      if (typeof poId === 'string' && poId.startsWith('PO-')) {
                        const match = poId.match(/(\d+)$/);
                        if (match && match[1]) {
                          poId = parseInt(match[1], 10);
                        }
                      } else if (typeof poId === 'string') {
                        poId = parseInt(poId, 10);
                      }
                      
                      const response = await CommonService.post(
                        `/purchase-order/${poId}/send-message`,
                        { message: messageText.trim() }
                      );
                      
                      if (response && response.success) {
                        toast.success('Message sent successfully!');
                    setIsMessageModalOpen(false);
                    setMessageText('');
                      } else {
                        toast.error(response?.message || 'Failed to send message');
                      }
                    } catch (error) {
                      console.error('Error sending message:', error);
                      toast.error(error?.response?.data?.error || error?.message || 'Failed to send message');
                    } finally {
                      setSendingMessage(false);
                    }
                  }
                }}
                disabled={!messageText.trim() || sendingMessage}
              >
                {sendingMessage ? (
                  <>
                    <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="2" x2="12" y2="6"></line>
                      <line x1="12" y1="18" x2="12" y2="22"></line>
                      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                      <line x1="2" y1="12" x2="6" y2="12"></line>
                      <line x1="18" y1="12" x2="22" y2="12"></line>
                      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Vendor Modal */}
      {isContactVendorModalOpen && (
        <div className="po-status-modal-overlay" onClick={() => setIsContactVendorModalOpen(false)}>
          <div className="po-status-modal" onClick={(e) => e.stopPropagation()}>
            <div className="po-status-modal-header">
              <h3 className="po-status-modal-title">Vendor Contact Details</h3>
              <button 
                className="po-status-modal-close"
                onClick={() => setIsContactVendorModalOpen(false)}
                aria-label="Close modal"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="po-status-modal-content">
              <div className="po-status-modal-recipient">
                <p className="po-status-modal-recipient-label">Vendor:</p>
                <p className="po-status-modal-recipient-name">{vendorCompany}</p>
              </div>
              <div className="po-status-modal-contact-details">
                <div className="po-status-modal-contact-item">
                  <div className="po-status-modal-contact-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                  </div>
                  <div className="po-status-modal-contact-info">
                    <label className="po-status-modal-contact-label">Email</label>
                    <p className="po-status-modal-contact-value">{sellerContact.email}</p>
                  </div>
                </div>
                <div className="po-status-modal-contact-item">
                  <div className="po-status-modal-contact-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                  </div>
                  <div className="po-status-modal-contact-info">
                    <label className="po-status-modal-contact-label">Phone Number</label>
                    <p className="po-status-modal-contact-value">{sellerContact.phone}</p>
                  </div>
                </div>
                <div className="po-status-modal-contact-item">
                  <div className="po-status-modal-contact-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </div>
                  <div className="po-status-modal-contact-info">
                    <label className="po-status-modal-contact-label">Address</label>
                    <p className="po-status-modal-contact-value">{sellerContact.address}</p>
                  </div>
                </div>
              </div>
              <div className="po-status-modal-footer">
                <button 
                  className="po-status-btn po-status-btn-secondary"
                  onClick={() => setIsContactVendorModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

